import type { SvgIconComponent } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IncompleteCircleIcon from '@mui/icons-material/IncompleteCircle';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';

import { CoverageEnum, OutcomeEnum } from '~/data/generated/model';
import type { MaintenanceVisitPeriod } from '~/data/schemas/maintenanceVisit';

interface OutcomeMeta {
  label: string;
  shortLabel: string;
  Icon: SvgIconComponent;
  color: string;
  description: string;
}

// Keyed on the outcome the API sends rather than re-derived here: the precedence between
// a record, a mark and a period that has not closed is stated once, on the server.
const OUTCOME_META: Record<OutcomeEnum, OutcomeMeta> = {
  [OutcomeEnum.done]: {
    label: 'Requirement met',
    shortLabel: 'Done',
    Icon: CheckCircleIcon,
    color: 'success.main',
    description: 'The records we hold meet what the contract asks for this period.',
  },
  [OutcomeEnum.partial]: {
    label: 'Partially done',
    shortLabel: 'Partial',
    Icon: IncompleteCircleIcon,
    color: 'warning.main',
    description: 'Some of the work reached us, but less than the contract asks for.',
  },
  [OutcomeEnum.missing]: {
    label: 'No record',
    shortLabel: 'No record',
    Icon: HelpOutlineIcon,
    color: 'text.disabled',
    description: 'Nothing reached us for this period, and nobody has told us why.',
  },
  [OutcomeEnum.skipped]: {
    label: 'Skipped visit',
    shortLabel: 'Skipped',
    Icon: CancelIcon,
    color: 'error.main',
    description: 'The maintenance did not happen, and somebody said so.',
  },
  [OutcomeEnum.open]: {
    label: 'Period still running',
    shortLabel: 'Open',
    Icon: PendingOutlinedIcon,
    color: 'text.disabled',
    description: 'This period has not closed yet, so there is nothing to answer for.',
  },
};

// A kind the API added ahead of this build would otherwise throw on an unguarded lookup
// and take the whole grid down over one cell.
export const outcomeMeta = (outcome: string): OutcomeMeta =>
  OUTCOME_META[outcome as OutcomeEnum] ?? OUTCOME_META[OutcomeEnum.missing];

const unitWord = (quantity: number, unit: string) => {
  const word = unit === 'visits' ? 'visit' : 'hour';
  return quantity === 1 ? word : `${word}s`;
};

/** "2 visits / 3 months" — what the contract asks for, in its own unit. */
export const requirementLabel = (row: {
  requiredQuantity: number;
  requiredUnit: string;
  periodMonths: number;
}): string =>
  `${row.requiredQuantity} ${unitWord(row.requiredQuantity, row.requiredUnit)} / ${
    row.periodMonths === 1 ? 'month' : `${row.periodMonths} months`
  }`;

/**
 * "1 of 2" on a period that falls short, so the shortfall is readable in the cell
 * rather than only in the modal. A met requirement needs no number.
 */
export const achievementLabel = (
  period: Pick<MaintenanceVisitPeriod, 'coverage' | 'achievedQuantity'> & {
    requiredQuantity: number;
  },
): string | null =>
  period.coverage === CoverageEnum.complete
    ? null
    : `${period.achievedQuantity} of ${period.requiredQuantity}`;
